import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  TrendingUp,
  Users,
  Clock,
  DollarSign,
  Target,
  Sparkles,
  ArrowUpRight,
} from "lucide-react";
import { Slider } from "../ui/slider";

const formatMoney = (value) => {
  const number = Number(value) || 0;

  return number.toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  });
};

const formatCompactMoney = (value) => {
  const number = Number(value) || 0;

  if (number >= 10000000) {
    return `₹${(number / 10000000).toFixed(1)} Cr`;
  }

  if (number >= 100000) {
    return `₹${(number / 100000).toFixed(1)} L`;
  }

  if (number >= 1000) {
    return `₹${(number / 1000).toFixed(1)} K`;
  }

  return `₹${Math.round(number).toLocaleString("en-IN")}`;
};

function ResultCard({
  icon,
  title,
  value,
  subtitle,
  accent = "cyan",
}) {
  const accentClasses = {
    cyan: "text-cyan-300",
    pink: "text-pink-300",
    purple: "text-purple-300",
    yellow: "text-yellow-300",
  };

  return (
    <div className="glass rounded-2xl p-5 border border-white/10 hover:border-cyan-400/30 transition-all">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-white/45">
          {title}
        </div>

        <div className={accentClasses[accent] || "text-cyan-300"}>
          {icon}
        </div>
      </div>

      <div
        className={`mt-3 text-3xl font-[Outfit] font-bold ${
          accentClasses[accent] || "text-cyan-300"
        }`}
      >
        {value}
      </div>

      <div className="mt-2 text-xs text-white/45">
        {subtitle}
      </div>
    </div>
  );
}

function ProjectionBar({ month, revenue, maxRevenue }) {
  const width =
    maxRevenue > 0
      ? Math.max(4, Math.min(100, (revenue / maxRevenue) * 100))
      : 4;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">
          Month {month}
        </span>

        <span className="text-sm font-semibold text-white">
          ₹{formatMoney(revenue)}
        </span>
      </div>

      <div className="h-3 rounded-full bg-white/[0.05] overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${width}%`,
            background:
              "linear-gradient(90deg,#00F0FF,#B200FF,#FF007F)",
            boxShadow:
              "0 0 18px rgba(0,240,255,.35)",
          }}
        />
      </div>
    </div>
  );
}

export default function Simulator() {
  const [revenue, setRevenue] = useState([50000]);
  const [budget, setBudget] = useState([5000]);
  const [employees, setEmployees] = useState([10]);
  const [visitors, setVisitors] = useState([8000]);
  const [adoption, setAdoption] = useState([60]);

  const [output, setOutput] = useState(null);
  const [loading, setLoading] = useState(false);

  const rows = useMemo(
    () => [
      {
        l: "Monthly Revenue",
        v: revenue,
        set: setRevenue,
        min: 5000,
        max: 1000000,
        step: 1000,
        fmt: (x) => `₹${formatMoney(x)}`,
        key: "rev",
      },
      {
        l: "Marketing Budget",
        v: budget,
        set: setBudget,
        min: 500,
        max: 200000,
        step: 500,
        fmt: (x) => `₹${formatMoney(x)}`,
        key: "bud",
      },
      {
        l: "Employees",
        v: employees,
        set: setEmployees,
        min: 1,
        max: 500,
        step: 1,
        fmt: (x) => `${x}`,
        key: "emp",
      },
      {
        l: "Website Visitors / mo",
        v: visitors,
        set: setVisitors,
        min: 100,
        max: 1000000,
        step: 100,
        fmt: (x) => x.toLocaleString("en-IN"),
        key: "vis",
      },
      {
        l: "AI Adoption %",
        v: adoption,
        set: setAdoption,
        min: 0,
        max: 100,
        step: 1,
        fmt: (x) => `${x}%`,
        key: "adp",
      },
    ],
    [revenue, budget, employees, visitors, adoption]
  );

  const computeProjection = () => {
    setLoading(true);

    try {
      const monthlyRevenue = Number(revenue[0]) || 0;
      const marketingBudget = Number(budget[0]) || 0;
      const employeeCount = Number(employees[0]) || 0;
      const websiteVisitors = Number(visitors[0]) || 0;
      const aiAdoption = Number(adoption[0]) || 0;

      /*
       * MUNNEXA PROJECTION MODEL
       *
       * This is an illustrative business projection.
       *
       * AI adoption influences:
       * - conversion improvement
       * - operational efficiency
       * - customer acquisition
       * - hours saved
       */

      const baseConversion =
        websiteVisitors > 0
          ? monthlyRevenue / Math.max(websiteVisitors, 1)
          : 0;

      const adoptionFactor = aiAdoption / 100;

      const conversionLift =
        1 + adoptionFactor * 0.35;

      const projectedMonthlyRevenue =
        monthlyRevenue * conversionLift;

      const ninetyDayRevenue =
        projectedMonthlyRevenue * 3;

      const additionalRevenue =
        ninetyDayRevenue -
        monthlyRevenue * 3;

      const estimatedMarketingReturn =
        marketingBudget * 3;

      const efficiencySavings =
        employeeCount *
        160 *
        adoptionFactor *
        0.30;

      const estimatedLabourSavings =
        efficiencySavings * 3 * 25;

      const operationalGain =
        additionalRevenue +
        estimatedLabourSavings;

      const totalInvestment =
        estimatedMarketingReturn;

      const projectedProfit =
        operationalGain -
        totalInvestment;

      const visitorConversionRate =
        websiteVisitors > 0
          ? (monthlyRevenue / websiteVisitors) * 100
          : 0;

      const customerValue =
        visitorConversionRate > 0
          ? monthlyRevenue /
            Math.max(
              websiteVisitors *
                (visitorConversionRate / 100),
              1
            )
          : 0;

      const newCustomers =
        Math.round(
          websiteVisitors *
            3 *
            Math.min(
              0.15,
              Math.max(
                0.01,
                visitorConversionRate / 100
              )
            ) *
            conversionLift
        );

      const hoursSaved =
        Math.round(
          employeeCount *
            160 *
            adoptionFactor *
            0.30 *
            3
        );

      const roi =
        totalInvestment > 0
          ? (projectedProfit / totalInvestment) *
            100
          : 0;

      const monthlyProjection = [];

      for (let month = 1; month <= 3; month += 1) {
        const growth =
          Math.pow(
            1 + adoptionFactor * 0.04,
            month
          );

        monthlyProjection.push({
          month,
          revenue:
            projectedMonthlyRevenue * growth,
        });
      }

      const result = {
        projected_revenue: Math.max(
          0,
          ninetyDayRevenue
        ),

        projected_profit: Math.max(
          0,
          projectedProfit
        ),

        new_customers: Math.max(
          0,
          newCustomers
        ),

        automation_hours_saved: Math.max(
          0,
          hoursSaved
        ),

        roi_percent: Math.max(
          0,
          roi
        ),

        monthly_projection:
          monthlyProjection,

        additional_revenue:
          Math.max(0, additionalRevenue),

        efficiency_savings:
          Math.max(0, estimatedLabourSavings),

        conversion_rate:
          visitorConversionRate,

        customer_value:
          customerValue,

        ai_adoption:
          aiAdoption,

        monthly_revenue:
          monthlyRevenue,

        marketing_budget:
          marketingBudget,

        employees:
          employeeCount,

        visitors:
          websiteVisitors,
      };

      setOutput(result);

      toast.success(
        "NEXA growth projection completed"
      );
    } catch (error) {
      console.error(
        "Projection calculation error:",
        error
      );

      toast.error(
        "Unable to calculate projection."
      );
    }

    setLoading(false);
  };

  const maxProjectionRevenue = output
    ? Math.max(
        ...output.monthly_projection.map(
          (item) => item.revenue
        )
      )
    : 1;

  return (
    <section
      id="pricing"
      className="relative py-28 px-6"
      data-testid="simulator-section"
    >
      <div className="max-w-7xl mx-auto">

        {/* HEADER */}
        <div className="text-center mb-14">

          <p className="font-mono text-[11px] tracking-[0.4em] text-cyan-300/80 mb-3">
            ◆ NEXA REVENUE SIMULATOR
          </p>

          <h2 className="text-4xl sm:text-5xl font-[Outfit] font-bold">
            Model your next era — in{" "}
            <span className="text-gradient">
              real time.
            </span>
          </h2>

          <p className="mt-4 text-white/60 max-w-2xl mx-auto">
            Adjust your business metrics and discover
            how AI adoption can influence revenue,
            profitability, customer acquisition and
            operational efficiency.
          </p>

        </div>

        {/* MAIN GRID */}
        <div className="grid lg:grid-cols-2 gap-6">

          {/* CONTROLS */}
          <div className="glass holo-border rounded-3xl p-6 sm:p-8 space-y-7">

            <div>

              <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-300/70">
                BUSINESS INPUTS
              </div>

              <h3 className="mt-2 text-2xl font-bold text-white">
                Configure your business
              </h3>

              <p className="mt-1 text-sm text-white/40">
                Move the sliders to model different
                growth scenarios.
              </p>

            </div>

            {rows.map((r) => (
              <div
                key={r.key}
                data-testid={`sim-row-${r.key}`}
              >

                <div className="flex items-center justify-between mb-3">

                  <span className="font-mono text-[11px] uppercase tracking-widest text-white/55">
                    {r.l}
                  </span>

                  <span className="font-[Outfit] font-semibold text-cyan-300">
                    {r.fmt(r.v[0])}
                  </span>

                </div>

                <Slider
                  value={r.v}
                  onValueChange={r.set}
                  min={r.min}
                  max={r.max}
                  step={r.step}
                  className="cursor-pointer"
                  data-testid={`sim-slider-${r.key}`}
                />

              </div>
            ))}

            {/* BUTTON */}
            <button
              onClick={computeProjection}
              disabled={loading}
              data-testid="sim-compute-btn"
              className="btn-gradient w-full rounded-full py-4 font-semibold text-base disabled:opacity-60"
            >
              {loading
                ? "Calculating NEXA Projection..."
                : "Project My Growth →"}
            </button>

            <div className="rounded-2xl bg-cyan-400/[0.04] border border-cyan-300/10 p-4">

              <div className="flex gap-3">

                <Sparkles
                  size={18}
                  className="text-cyan-300 mt-0.5"
                />

                <p className="text-xs leading-6 text-white/45">
                  NEXA uses your business inputs to
                  generate an illustrative 90-day growth
                  scenario. Actual results depend on
                  market conditions and execution.
                </p>

              </div>

            </div>

          </div>

          {/* RESULTS */}
          <div className="glass-strong holo-border rounded-3xl p-6 sm:p-8 relative overflow-hidden">

            <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />

            <div className="relative">

              <div className="flex items-center justify-between">

                <div>

                  <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-300/80">
                    NEXA PROJECTION
                  </div>

                  <h3 className="mt-2 text-2xl font-bold text-white">
                    90-Day Growth Outlook
                  </h3>

                </div>

                <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-300/20 grid place-items-center">
                  <TrendingUp
                    size={20}
                    className="text-cyan-300"
                  />
                </div>

              </div>

              {!output && (
                <div className="mt-8 rounded-2xl bg-white/[0.025] border border-white/10 p-8 text-center">

                  <div className="w-14 h-14 mx-auto rounded-2xl bg-fuchsia-500/10 border border-fuchsia-300/20 grid place-items-center">

                    <Target
                      size={24}
                      className="text-fuchsia-300"
                    />

                  </div>

                  <h4 className="mt-4 text-lg font-semibold text-white">
                    Ready to project your growth
                  </h4>

                  <p className="mt-2 text-sm leading-6 text-white/40">
                    Adjust your business metrics and
                    click Project My Growth to generate
                    your NEXA forecast.
                  </p>

                </div>
              )}

              {output && (
                <div className="mt-6 space-y-5">

                  {/* KPI CARDS */}
                  <div className="grid grid-cols-2 gap-4">

                    <ResultCard
                      icon={<DollarSign size={18} />}
                      title="Projected Revenue"
                      value={formatCompactMoney(
                        output.projected_revenue
                      )}
                      subtitle="90-day opportunity"
                      accent="pink"
                    />

                    <ResultCard
                      icon={<TrendingUp size={18} />}
                      title="Projected Profit"
                      value={formatCompactMoney(
                        output.projected_profit
                      )}
                      subtitle="Estimated incremental profit"
                      accent="cyan"
                    />

                    <ResultCard
                      icon={<Users size={18} />}
                      title="New Customers"
                      value={output.new_customers.toLocaleString(
                        "en-IN"
                      )}
                      subtitle="Potential customers"
                      accent="purple"
                    />

                    <ResultCard
                      icon={<Clock size={18} />}
                      title="Hours Saved"
                      value={output.automation_hours_saved.toLocaleString(
                        "en-IN"
                      )}
                      subtitle="Estimated automation impact"
                      accent="yellow"
                    />

                  </div>

                  {/* ROI */}
                  <div
                    className="glass rounded-2xl p-5"
                    data-testid="sim-out-roi"
                  >

                    <div className="flex items-center justify-between">

                      <div>

                        <div className="text-[10px] font-mono uppercase text-white/45">
                          ROI · 90 DAYS
                        </div>

                        <div className="mt-2 text-5xl font-[Outfit] font-bold text-gradient">
                          {output.roi_percent.toFixed(1)}%
                        </div>

                      </div>

                      <div className="w-12 h-12 rounded-xl bg-fuchsia-500/10 border border-fuchsia-300/20 grid place-items-center">

                        <ArrowUpRight
                          size={24}
                          className="text-fuchsia-300"
                        />

                      </div>

                    </div>

                    <div className="mt-5 h-3 rounded-full bg-white/5 overflow-hidden">

                      <div
                        className="h-full tube transition-all duration-700"
                        style={{
                          width: `${Math.min(
                            100,
                            Math.max(
                              5,
                              output.roi_percent / 5
                            )
                          )}%`,
                        }}
                      />

                    </div>

                  </div>

                  {/* MONTHLY PROJECTION */}
                  <div className="glass rounded-2xl p-5">

                    <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-300/70">
                      REVENUE TRAJECTORY
                    </div>

                    <h4 className="mt-2 text-lg font-semibold text-white">
                      Next 90 Days
                    </h4>

                    <div className="mt-5 space-y-5">

                      {output.monthly_projection.map(
                        (item) => (
                          <ProjectionBar
                            key={item.month}
                            month={item.month}
                            revenue={item.revenue}
                            maxRevenue={
                              maxProjectionRevenue
                            }
                          />
                        )
                      )}

                    </div>

                  </div>

                  {/* AI IMPACT */}
                  <div className="rounded-2xl p-5 bg-gradient-to-br from-fuchsia-500/10 via-purple-500/10 to-cyan-500/10 border border-fuchsia-400/20">

                    <div className="flex items-center gap-3">

                      <div className="w-10 h-10 rounded-xl bg-fuchsia-500/10 border border-fuchsia-300/20 grid place-items-center">

                        <Sparkles
                          size={19}
                          className="text-fuchsia-300"
                        />

                      </div>

                      <div>

                        <div className="font-mono text-[10px] uppercase tracking-widest text-fuchsia-300/80">
                          NEXA AI IMPACT
                        </div>

                        <h4 className="text-lg font-bold text-white">
                          AI Adoption:{" "}
                          {output.ai_adoption}%
                        </h4>

                      </div>

                    </div>

                    <div className="mt-5 grid grid-cols-2 gap-3">

                      <div className="rounded-xl bg-black/20 p-4">

                        <div className="text-[10px] uppercase text-white/40">
                          Additional Revenue
                        </div>

                        <div className="mt-1 text-lg font-bold text-cyan-300">
                          {formatCompactMoney(
                            output.additional_revenue
                          )}
                        </div>

                      </div>

                      <div className="rounded-xl bg-black/20 p-4">

                        <div className="text-[10px] uppercase text-white/40">
                          Efficiency Gain
                        </div>

                        <div className="mt-1 text-lg font-bold text-pink-300">
                          {formatCompactMoney(
                            output.efficiency_savings
                          )}
                        </div>

                      </div>

                    </div>

                  </div>

                  <p className="text-[11px] leading-5 text-white/30">
                    Projection figures are illustrative and
                    should not be treated as guaranteed
                    financial results. Actual outcomes will
                    depend on your market, conversion rates,
                    customer behaviour and implementation.
                  </p>

                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
